import { useProjectParams } from '@hooks/useProjectParams';
import { DropdownContext } from 'components/DropdownContext';
import { ButtonWithTooltip } from 'components/Form/ButtonWithTooltip';
import {
    findAllConstructionTypesFromObjects,
    WorkOrderConstructionTypesFromObjects,
} from 'components/WorkOrderConstructionTypesTabSelect';
import { ContractStatusEnum, ProjectStatusEnum, WorkOrderChangeStatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { usePageFilterSaver } from 'hooks/usePageFilterSaver';
import { useProjectData } from 'hooks/useProjectData';
import { PageHeaderLayoutContext } from 'pages/Project/Common/Layouts/PageLayoutContext';
import { ProjectPageLayout } from 'pages/Project/Common/Layouts/ProjectPageLayout';
import { cnMixSpace, IconArrowDown } from 'uikit';
import { workOrderChangeDescriptionNumberAndDate } from 'utils/descriptionNumberAndDate';
import { getLabelWorkOrderNoSingingDate } from 'utils/getLabelWorkOrder';

import type { AgGridReact } from 'ag-grid-react';
import * as R from 'ramda';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useContractData } from '../../Contract/hooks/useContractData';
import { WorkOrderFilter } from '../../WorkOrder/WorkOrderFilter';
import type { WorkOrderFilterType } from '../../WorkOrder/WorkOrderFilter/types';
import { WorkOrderFilterLevelEnum } from '../../WorkOrder/WorkOrderFilter/types';
import { WorkOrderUseCoefficient } from '../../WorkOrderUseCoefficientModal/WorkOrderUseCoefficient';
import { AddWorkOrderChangeOther } from '../AddWorkOrderChangeOther';
import { AddWorkOrderChangeUer } from '../AddWorkOrderChangeUer';
import { useGetWorkOrderChange, useWorkOrderChangeObjects, useWorkOrderChangeSeasons } from '../hooks';
import { MoveVolumesBetweenSeasonsForObject } from '../MoveVolumesBetweenSeasonsForObject';
import { WorkOrderChangeDraftInformer } from '../WorkOrderChangeDraftInformer';
import { WorkOrderChangeSummaryTable } from '../WorkOrderChangeSummaryTableNew';
import { WorkOrderChangeTable } from '../WorkOrderChangeTable';
import { WorkOrderChangeToolbar } from '../WorkOrderChangeToolbar';

import { ModalName, useAddItemsToWorkOrderChangeMenu } from './hooks/useAddItemsToWorkOrderChangeMenu';
import { useCorrectUpdateWorkOrderChangeUerData } from './hooks/useCorrectUpdateWorkOrderChangeUerData';

import '../workOrderChange.css';

const WORK_ORDER_CHANGE_PATH_PATTERN = '/projects/:methodology/:id/orderChange/:orderId';

/** Long из GraphQL и number из фильтра/сохранённого состояния при строгом === часто не совпадают */
const isSameGraphqlLongId = (a: unknown, b: unknown) => a != null && b != null && String(a) === String(b);

/** У корня дерева часто задан только objectUniqueId; у потомков rootObjectUniqueId ссылается на корень */
const rootTreeUniqueIdFromObject = (root: { rootObjectUniqueId?: string | null; objectUniqueId?: string | null }) =>
    root.rootObjectUniqueId?.trim() || root.objectUniqueId?.trim() || undefined;

export const WorkOrderChangeNew = () => {
    const filterRef = useRef<HTMLDivElement>(null);
    const gridTableRef = useRef<AgGridReact>(null);
    const refreshDraftInfoRef = useRef<{ refreshDraftInfo: () => void }>();
    const [position, setPosition] = useState<number | undefined>();

    const { isHeaderOpened: isContextHeaderOpen } = useContext(PageHeaderLayoutContext);

    const refreshCurrentTableData = useCallback(() => {
        if (gridTableRef?.current) {
            gridTableRef.current?.api?.refreshInfiniteCache();
        }

        if (refreshDraftInfoRef?.current?.refreshDraftInfo) {
            refreshDraftInfoRef?.current?.refreshDraftInfo();
        }
    }, [gridTableRef]);

    const { id: projectId, orderChangeId, methodology } = useProjectParams();

    const { projectData, refreshProject } = useProjectData(projectId);

    const [contractData] = useContractData(projectData?.contractId);

    const catalogUerBaseName = contractData?.catalogUerBase?.name ?? '';
    const catalogVersion = contractData?.catalogUerBase?.version ?? '';

    const hasWorkKind = useMemo(
        () => projectData?.contractNavigation?.data?.contractNavData?.metadata?.hasWorkKind,
        [projectData?.contractNavigation?.data?.contractNavData?.metadata?.hasWorkKind],
    );

    const { workOrderChangeUer: workOrderChangeUerRules } = useAccess();

    const contractId = useMemo(() => projectData?.contractId, [projectData?.contractId]);
    const statusProject = projectData?.status;
    const statusContract = projectData?.contractNavigation?.data?.contractNavData?.status;

    const isProjectStatusDelayed = statusProject === ProjectStatusEnum.Delayed;
    const isProjectStatusCompleted = statusProject === ProjectStatusEnum.Completed;

    const isContractStatusWork = statusContract === ContractStatusEnum.InImplementation;

    const [constructionTypeId, setConstructionTypeId] = useState(0);
    const [filters, setFilters] = useState<WorkOrderFilterType>({});
    const workOrderChangeId = useMemo(() => Number(orderChangeId), [orderChangeId]);

    const { refreshWorkOrderChangeSeasonsHandler: refreshSeasonsDataHandler, seasons: workOrderChangeSeasons } =
        useWorkOrderChangeSeasons(workOrderChangeId);

    const { workOrderChangeData, refreshWorkOrderChangeData } = useGetWorkOrderChange({
        workOrderChangeId,
    });

    const { workOrderChangeObjectsDataPatched, workOrderChangeObjectsData, refreshWorkOrderChangeObjects } =
        useWorkOrderChangeObjects({
            workOrderChangeId,
        });

    const workOrderChangeStatusOnFormation = workOrderChangeData?.status === WorkOrderChangeStatusEnum.OnFormation;

    const checkedWorkOrderNavs = useMemo(
        () =>
            projectData?.contractNavigation?.data?.workOrderNavs?.find(
                (workOrder) => workOrder.id === workOrderChangeData?.workOrderId,
            ),
        [projectData?.contractNavigation?.data?.workOrderNavs, workOrderChangeData?.workOrderId],
    );

    const isDisabledByWorkOrderStatus = checkedWorkOrderNavs?.status === WorkOrderStatusEnum.Delayed;

    const titleWorkOrderChange = useMemo(
        () =>
            `Изм ${workOrderChangeDescriptionNumberAndDate(
                workOrderChangeData?.internalNumber,
                workOrderChangeData?.signingDate,
                workOrderChangeData?.workOrderNumberText,
            )}`,
        [
            workOrderChangeData?.internalNumber,
            workOrderChangeData?.signingDate,
            workOrderChangeData?.workOrderNumberText,
        ],
    );

    const breadCrumbsItems = useMemo(
        () => [
            {
                label: projectData?.name,
                href: `/projects/${methodology}/${projectId}`,
            },
            {
                label: 'Договор',
                href: `/projects/${methodology}/${projectData?.uniqueId}/contract`,
            },
            {
                label: getLabelWorkOrderNoSingingDate(checkedWorkOrderNavs),
                href: `/projects/${methodology}/${projectData?.uniqueId}/order/${checkedWorkOrderNavs?.id}`,
            },
        ],
        [projectData?.name, projectData?.uniqueId, methodology, projectId, checkedWorkOrderNavs],
    );

    const { savedFilterState, handleSaveFilterState } = usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: ({ savedValuesObj }) => {
                    const savedConstructionTypeId = savedValuesObj?.constructionTypeId;
                    const savedFilters = savedValuesObj?.[String(savedConstructionTypeId)] ?? {};

                    if (savedConstructionTypeId) {
                        setConstructionTypeId(savedConstructionTypeId);
                    }

                    setFilters((prevState) => {
                        if (R.equals(prevState, savedFilters) && savedFilters) {
                            return prevState;
                        }

                        return savedFilters;
                    });
                },
                withEmptyFilterSetter: (setter: (params: { key: string | undefined; newState: any }) => void) => {
                    const newConstructionTypeId = findAllConstructionTypesFromObjects({
                        workOrderChangeObjectsData,
                    })?.[0]?.id;

                    if (newConstructionTypeId) {
                        setConstructionTypeId(newConstructionTypeId);
                        setter({
                            key: 'constructionTypeId',
                            newState: newConstructionTypeId,
                        });
                    }
                },
                matchPathPattern: WORK_ORDER_CHANGE_PATH_PATTERN,
                unableToProcess: !workOrderChangeObjectsData?.length,
            }),
            [workOrderChangeObjectsData],
        ),
    );

    const changeConstructionTypeHandler = useCallback(
        (newConstructionType) => {
            setConstructionTypeId(newConstructionType.id);

            setFilters(savedFilterState?.[newConstructionType.id] ?? {});

            handleSaveFilterState({
                key: `constructionTypeId`,
                newState: newConstructionType?.id,
            });
        },
        [handleSaveFilterState, savedFilterState],
    );

    const filterChangeHandler = useCallback(
        (newFilters: WorkOrderFilterType) => {
            let mergedFilters: WorkOrderFilterType = {};

            setFilters((oldFilters: WorkOrderFilterType) => {
                mergedFilters = { ...oldFilters, ...newFilters };

                return mergedFilters;
            });

            handleSaveFilterState({
                key: String(constructionTypeId),
                newState: mergedFilters,
            });
        },
        [constructionTypeId, handleSaveFilterState],
    );

    const isSummaryChapterSelected = useMemo(
        () => filters?.level?.value === WorkOrderFilterLevelEnum.TOTALS,
        [filters?.level?.value],
    );

    const actionsDisabled = useMemo(
        () =>
            !workOrderChangeStatusOnFormation ||
            !isContractStatusWork ||
            isProjectStatusDelayed ||
            isProjectStatusCompleted ||
            isDisabledByWorkOrderStatus,
        [
            isContractStatusWork,
            isDisabledByWorkOrderStatus,
            isProjectStatusCompleted,
            isProjectStatusDelayed,
            workOrderChangeStatusOnFormation,
        ],
    );

    const isMoveVolumesDisabled = useMemo(
        () => !workOrderChangeStatusOnFormation || workOrderChangeSeasons.length <= 1 || isDisabledByWorkOrderStatus,
        [isDisabledByWorkOrderStatus, workOrderChangeSeasons.length, workOrderChangeStatusOnFormation],
    );

    const costRounding = useMemo(
        () => projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding ?? 0,
        [projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding],
    );

    const [isMoveVolumesOpened, setIsMoveVolumesOpened] = useState(false);

    const onMoveVolumesClick = useCallback(() => setIsMoveVolumesOpened(true), []);
    const onMoveVolumesClose = useCallback(() => setIsMoveVolumesOpened(false), []);

    useEffect(() => {
        if (filterRef.current) {
            setPosition(filterRef.current?.getBoundingClientRect?.().bottom);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterRef, isContextHeaderOpen, filterRef.current?.getBoundingClientRect?.().bottom]);

    const { openedModal, closeModalsHandler, menuItems } = useAddItemsToWorkOrderChangeMenu({
        filters,
        actionsDisabled,
        workOrderChangeUerRules,
    });

    const objSelected = useMemo(
        () => workOrderChangeObjectsDataPatched?.find((o) => isSameGraphqlLongId(o.id, filters?.objectId)),
        [filters?.objectId, workOrderChangeObjectsDataPatched],
    );

    const objectFilter = useMemo(() => {
        const subObjSelected = workOrderChangeObjectsDataPatched?.find((so) =>
            isSameGraphqlLongId(so.id, filters?.subObjectId),
        );

        if (subObjSelected && objSelected) {
            return {
                rootObjectUniqueId: rootTreeUniqueIdFromObject(objSelected),
                objectUniqueId: subObjSelected.objectUniqueId?.trim() || undefined,
                workOrderRootObjectId: objSelected?.workOrderObjectId,
                workOrderChangeRootObjectId: objSelected?.workOrderChangeObjectId,
                workOrderObjectId: subObjSelected?.workOrderObjectId,
                workOrderChangeObjectId: subObjSelected?.workOrderChangeObjectId,
            };
        }

        if (objSelected) {
            return {
                rootObjectUniqueId: rootTreeUniqueIdFromObject(objSelected),
                workOrderRootObjectId: objSelected?.workOrderObjectId,
                workOrderChangeRootObjectId: objSelected?.workOrderChangeObjectId,
            };
        }

        return null;
    }, [filters?.subObjectId, objSelected, workOrderChangeObjectsDataPatched]);

    const { makeCorrectRefreshTableData } = useCorrectUpdateWorkOrderChangeUerData({
        workOrderObjectId: objSelected?.workOrderObjectId,
        workOrderChangeObjectId: objSelected?.workOrderChangeObjectId,
        objectsData: workOrderChangeObjectsDataPatched,
        refreshWorkOrderChangeObjects,
        refreshTableFn: refreshCurrentTableData,
    });

    return (
        <ProjectPageLayout
            title={titleWorkOrderChange}
            statusWorkOrderChange={workOrderChangeData?.status}
            breadCrumbsItems={breadCrumbsItems}
            style={{ marginBottom: 0 }}
            isHeaderCollapsable
            layoutEntityKey={orderChangeId}
            headerRightSide={
                <WorkOrderChangeDraftInformer
                    workOrderChangeId={workOrderChangeId}
                    status={workOrderChangeData?.status}
                    refreshDraftInfoRef={refreshDraftInfoRef}
                />
            }
            headerBottom={
                <WorkOrderChangeToolbar
                    refreshProject={refreshProject}
                    workOrderChangeData={workOrderChangeData}
                    projectData={projectData}
                    refreshWorkOrderChangeData={refreshWorkOrderChangeData}
                    isActionsDisabled={actionsDisabled}
                    statusContract={statusContract}
                    statusProject={statusProject}
                    workOrderChangeUerRules={workOrderChangeUerRules}
                    refreshTable={refreshCurrentTableData}
                    refreshWorkOrderChangeObjects={refreshWorkOrderChangeObjects}
                    refreshSeasonsData={refreshSeasonsDataHandler}
                />
            }
        >
            <WorkOrderConstructionTypesFromObjects
                workOrderChangeObjectsData={workOrderChangeObjectsData}
                onChange={changeConstructionTypeHandler}
                activeConstructionTypeId={constructionTypeId}
            />

            <div
                ref={filterRef}
                className={cnMixSpace({ mT: 'xl', mB: 'xl' })}
                style={{ overflow: 'auto', display: 'flex' }}
            >
                <WorkOrderFilter
                    defaultValues={filters}
                    onChange={filterChangeHandler}
                    constructionTypeId={constructionTypeId}
                    workOrderObjects={workOrderChangeObjectsDataPatched}
                />
                {!isSummaryChapterSelected && (
                    <ButtonWithTooltip
                        tooltipText="Для переноса объемов выберите объект строительства в фильтре над таблицей"
                        onClick={onMoveVolumesClick}
                        label="Перенести объемы"
                        size="s"
                        view="secondary"
                        disabled={isMoveVolumesDisabled || !filters.objectId}
                        isTooltipHidden={(!filters.objectId && isMoveVolumesDisabled) || Boolean(filters.objectId)}
                        className={cnMixSpace({
                            mL: 'xl',
                        })}
                        classNameTooltip="workOrderChangeMoveVolumeTooltip"
                    />
                )}
                <DropdownContext
                    wrapperClassName={cnMixSpace({ mL: 'xl' })}
                    items={menuItems}
                    btnView="primary"
                    btnSize="s"
                    btnIconSize="s"
                    btnLabel="Добавить"
                    icon={IconArrowDown}
                    className="widthMenu"
                />

                {openedModal?.[ModalName.Price] && (
                    <AddWorkOrderChangeUer
                        constructionTypeId={constructionTypeId}
                        onClose={closeModalsHandler}
                        objects={workOrderChangeObjectsDataPatched}
                        workOrderChangeId={workOrderChangeId}
                        reloadWorkOrderTable={makeCorrectRefreshTableData}
                        objectId={filters?.objectId}
                        subObjectId={filters?.subObjectId}
                        contractId={projectData?.contractId}
                        catalogVersion={catalogVersion}
                        catalogName={catalogUerBaseName}
                    />
                )}

                {openedModal?.[ModalName.Other] && (
                    <AddWorkOrderChangeOther
                        onClose={closeModalsHandler}
                        constructionTypeId={constructionTypeId}
                        workOrderChangeId={workOrderChangeId}
                        reloadWorkOrderTable={refreshCurrentTableData}
                        workOrderChangeSeasons={workOrderChangeSeasons}
                    />
                )}
                <MoveVolumesBetweenSeasonsForObject
                    isOpen={isMoveVolumesOpened}
                    onClose={onMoveVolumesClose}
                    refreshTableDataHandler={() => {
                        makeCorrectRefreshTableData([]);
                    }}
                    workOrderChangeSeasons={workOrderChangeSeasons}
                    workOrderChangeId={workOrderChangeId}
                    workOrderRootObjectId={objSelected?.workOrderObjectId}
                    workOrderChangeRootObjectId={objSelected?.workOrderChangeObjectId}
                />
            </div>
            {openedModal?.[ModalName.UseCoefficient] && (
                <WorkOrderUseCoefficient
                    workOrderObjects={workOrderChangeObjectsDataPatched}
                    constructionTypeId={constructionTypeId}
                    closeHandler={closeModalsHandler}
                    workOrderChangeId={workOrderChangeId}
                    updaterApplyRatio={refreshCurrentTableData}
                    objectId={filters?.objectId}
                />
            )}
            <div
                id="workOrderChange"
                style={{
                    height: `calc(100vh - ${(position ?? 0) + 74}px)`,
                }}
            >
                {!isSummaryChapterSelected && (
                    <WorkOrderChangeTable
                        ref={gridTableRef}
                        workOrderChangeId={workOrderChangeId}
                        constructionTypeId={constructionTypeId}
                        objectFilterData={objectFilter}
                        costRounding={costRounding}
                        statusWorkOrderChange={workOrderChangeData?.status}
                        isActionsDisabled={actionsDisabled}
                        contractId={contractId}
                        refreshTable={makeCorrectRefreshTableData}
                        workOrderChangeSeasons={workOrderChangeSeasons}
                        refreshProject={refreshProject}
                        workOrderChangeObjectsData={workOrderChangeObjectsDataPatched}
                        hasWorkKind={hasWorkKind}
                    />
                )}

                {isSummaryChapterSelected && (
                    <WorkOrderChangeSummaryTable
                        workOrderChangeId={workOrderChangeId}
                        costRounding={costRounding}
                        workOrderChangeSeasons={workOrderChangeSeasons}
                    />
                )}
            </div>
        </ProjectPageLayout>
    );
};

WorkOrderChangeNew.displayName = 'WorkOrderChange';
